import React, {
    FC,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo,
} from 'react';

import Banner from '../../../../components/banners/BannerToolbar';
import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';
import { FormulaType } from '../../../../constants/formulaType';

// context
import { EditorContext } from '../../../../context/EditorContext';

// helpers
import random from '../../../../helpers/random';

import useMessageGenerationQuery from '../../../../hooks/formulas/useMessageGenerationQuery';
// hooks
import useHandleQueryParams from '../../../../hooks/helper/useHandleQueryParams';

// models
import {
    FormulaCoverSortOptions,
    FormulaPreviewCount,
} from '../../../../models/formula.model';

import MessagePreviewMain from '../MessagePreviewMain';
// components
import AnimatedMessageLoader from './AnimatedMessageLoader';
import SwirlingBackground from './SwirlingBackground';

interface CoverMainProps {
    formulaId: number;
}
const CoverMain: FC<PropsWithChildren<CoverMainProps>> = ({ formulaId }) => {
    const {
        data: previews,
        refetch,
        error,
    } = useMessageGenerationQuery(Number(formulaId), FormulaPreviewCount.max);
    const refreshMessages = () => {
        refetch();
    };

    const { editorDetails, setEditorDetails } = useContext(EditorContext);

    const { formula } = editorDetails;

    useEffect(() => {
        setEditorDetails({
            ...editorDetails,
            previews: previews || [],
            isPreviewError: Boolean(error),
            previewError: error?.message || null,
        });
    }, [previews, error]);

    const sampleQuantity = useMemo<number>(
        () =>
            previews?.length
                ? Math.min(FormulaPreviewCount.max, previews.length)
                : FormulaPreviewCount.max,
        [previews],
    );

    const randomSample = useMemo(
        () => random(previews, sampleQuantity),
        [previews, sampleQuantity],
    );

    const [queryParams] = useHandleQueryParams();

    const sortBy = useMemo<FormulaCoverSortOptions>(() => {
        const sortByParam = queryParams.sort_by;
        if (
            sortByParam &&
            [
                FormulaCoverSortOptions.Header,
                FormulaCoverSortOptions.Body,
                FormulaCoverSortOptions.Message,
                FormulaCoverSortOptions.Random,
            ].includes(sortByParam as FormulaCoverSortOptions)
        ) {
            return sortByParam as FormulaCoverSortOptions;
        } else {
            return FormulaCoverSortOptions.Random;
        }
    }, [queryParams.sort_by]);

    const sortedMessages = useMemo(() => {
        return [...randomSample].sort((a, b) => {
            if (a && b) {
                if (sortBy === FormulaCoverSortOptions.Random) {
                    return Math.random() - 0.5; // Random sorting
                } else {
                    const fieldA = a.content[sortBy]?.toLowerCase() || '';
                    const fieldB = b.content[sortBy]?.toLowerCase() || '';
                    return fieldA.localeCompare(fieldB);
                }
            }
            return 0;
        });
    }, [randomSample, sortBy]);

    return (
        <Container>
            <Banner
                title={'Your messages are shaping up nicely!'}
                description={'Add a few more to give your agents a choice.'}
                showRefresh={
                    !!formula?.message_count && formula?.message_count > 100
                }
                showFilter={randomSample.length > 24}
                refreshMessages={refreshMessages}
                formulaType={formula?.message_type}
            ></Banner>

            {formula?.message_type === FormulaType.email ? (
                <StyledMessagePreviewMain isCoverPreview />
            ) : (
                <>
                    <AnimatedMessageLoader
                        fetchedMessages={sortedMessages ?? []}
                    />
                    <SwirlingBackground />
                </>
            )}
        </Container>
    );
};

export default CoverMain;

const Container = styled.div({
    position: 'relative',
    width: '100%',
});

const StyledMessagePreviewMain = styled(MessagePreviewMain)({
    paddingTop: aampeTheme.spacing4(6),
});
